/**
 * `useHighlightTargetStore` — the cross-surface "go to this note and highlight
 * this quote" signal (2026-05-18 user story).
 *
 * The contract: when a user clicks a search result OR a chat citation pill that
 * carries quoted text, the click handler `setTarget({noteId, quotedText})`
 * BEFORE `navigate(toEditor(noteId))`. The Editor surface reads this on mount
 * (or when its noteId param changes), and if the stored noteId matches the
 * arriving noteId AND the request is recent (under 5s, so a stale store
 * doesn't fire on an unrelated navigation), it:
 *
 *   1. Forces `paneLayout = 'preview'` so the rendered markdown is visible.
 *   2. Finds the `quotedText` in the preview DOM and scrolls it to center.
 *   3. Wraps the matching range in `<mark class="highlight-pulse-orange">`
 *      for 10s, then unwraps (the incandescent orange pulse animation).
 *   4. Calls `clear()` so a refresh of the editor doesn't re-pulse.
 *
 * The store is intentionally simple — one slot, last-write-wins. If the user
 * clicks two results in rapid succession, the second wins; the first never
 * pulses. That matches the user's actual intent.
 *
 * The `requestedAt` timestamp is a freshness gate: if the user opens a result
 * (sets the store), then takes 30 seconds to navigate manually elsewhere and
 * back, we don't want the old highlight to fire. The Editor checks
 * `Date.now() - requestedAt < HIGHLIGHT_FRESHNESS_MS` before acting.
 */

import { create } from 'zustand';
import type { NoteId } from '@/types';

/**
 * Highlight intents older than this are stale — the Editor ignores them. 5s
 * is enough for a normal navigate (well under 1s) + first preview render but
 * short enough that a back/forward to an old route doesn't trigger a phantom
 * pulse on an unrelated visit.
 */
export const HIGHLIGHT_FRESHNESS_MS = 5_000;

export interface HighlightTarget {
  /** The note the user is navigating to. */
  noteId: NoteId;
  /** The quoted excerpt to scroll-and-pulse. May be empty if the source had no quote. */
  quotedText: string;
  /** `Date.now()` at the moment the click happened — drives the freshness check. */
  requestedAt: number;
}

interface HighlightTargetState {
  target: HighlightTarget | null;
  /** Set the next highlight target. Call this BEFORE `navigate(toEditor(noteId))`. */
  setTarget: (target: { noteId: NoteId; quotedText: string }) => void;
  /** Clear the target — the Editor calls this after consuming it. */
  clear: () => void;
}

export const useHighlightTargetStore = create<HighlightTargetState>((set) => ({
  target: null,
  setTarget: ({ noteId, quotedText }) =>
    set({ target: { noteId, quotedText, requestedAt: Date.now() } }),
  clear: () => set({ target: null }),
}));
